<template>
  <div class="season cricket-stats">
    <loading-state :state="state"></loading-state>
    <div v-if="stats">
      <h2>{{season.name}} Stats</h2>
      <cricket-season-menu :showSeason="true" :isMetric="isMetric" :seasonKey="seasonKey"></cricket-season-menu>

      <div class="head-info" v-if="stats.last_updated_date && stats.last_updated_date.iso">
        <p class="silent">Updated on {{stats.last_updated_date.iso | simpleDateTimeString}}</p>
      </div>

      <div v-if="stats.batting.best.length > 0">
        <div class="stats-box">
          <div class="item">
            <div class="number">{{stats.sixes}}</div>
            <div class="label">Sixes</div>
          </div>
          <div class="item">
            <div class="number">{{stats.fours}}</div>
            <div class="label">Fours</div>
          </div>
          <div class="item">
            <div class="number">{{stats.runs}}</div>
            <div class="label">Runs</div>
          </div>
        </div>

        <div class="stats-box">
          <div class="item">
            <h5>Best Batting</h5>
            <div class="line-item" 
              v-for="(stat, i) in stats.batting.best"
              :key="i"
              :class="{active:i==0}">
              <div class="label">
                <router-link class="clean no-top-left" :to="{name: 'cricketPlayer', params:{key: stat.key}}">
                  {{stat.name}}
                </router-link>
              </div>
              <div class="number large-number">
                <router-link
                  class="clean"
                  :to="{name:'cricketMatch', params:{key:stat.stats.batting.best.match}}">
                  <span class="highlight">{{stat.stats.batting.best.runs}}/{{stat.stats.batting.best.balls}}</span>
                  {{stat.stats.batting.best.fours}}x4s
                  {{stat.stats.batting.best.sixes}}x6s
                </router-link>
              </div>
            </div>
          </div>

          <div class="item">
            <h5>Best Bowling</h5>
            <div class="line-item" 
              v-for="(stat, i) in stats.bowling.best"
              :key="i"
              :class="{active:i==0}">
              <div class="label">
                <router-link class="clean no-top-left" :to="{name: 'cricketPlayer', params:{key: stat.key}}">
                  {{stat.name}}
                </router-link>
              </div>
              <div class="number large-number">
                <router-link
                  class="clean"
                  :to="{name:'cricketMatch', params:{key:stat.stats.bowling.best.match}}">
                <span class="highlight">{{stat.stats.bowling.best.wickets}}/{{stat.stats.bowling.best.balls | ballsAsOvers}}</span>
                ({{stat.stats.bowling.best.runs}} runs)
                </router-link>
              </div>
            </div>
          </div>
        </div>

        <div class="stats-box medium">
          <season-stats-item
            :stats="stats"
            name="Most Runs"
            statsGroup="batting"
            statsObject="most_runs"
            statsField="runs"
          ></season-stats-item>
          <season-stats-item
            :stats="stats"
            name="Most Wickets"
            statsGroup="bowling"
            statsObject="most_wickets"
            statsField="wickets"
          ></season-stats-item>
          <season-stats-item
            :stats="stats"
            name="Most Catches"
            statsGroup="fielding"
            statsObject="catches"
            statsField="catches"
          ></season-stats-item>
        </div>


        <div class="stats-box medium">
          <season-stats-item
            v-for="statsItem in battingStats"
            :stats="stats"
            :key="statsItem.key"
            statsGroup="batting"
            :name="'Most ' + statsItem.name"
            :statsObject="'most_' + statsItem.key"
            :statsField="statsItem.key"
          ></season-stats-item>
        </div>

        <div class="stats-box medium">
          <season-stats-item
            v-for="statsItem in bowlingStats"
            :stats="stats"
            :key="statsItem.key"
            statsGroup="bowling"
            :name="'Most ' + statsItem.name"
            :statsObject="'most_' + statsItem.key"
            :statsField="statsItem.key"
          ></season-stats-item>
          <season-stats-item
            :stats="stats"
            name="Least Dots"
            statsGroup="bowling"
            statsObject="fewest_dots"
            statsField="dots"
          ></season-stats-item>
        </div>

      </div>
      <div v-else>
        <div class="center">
          <p>Stats not available</p>
        </div>
      </div>


    </div>
  </div>
</template>
<script>
import { CricketSeason } from '@/models/cricket/season'
import { DataState, AppState } from '@/state'
import Utils from '@/utils'

import SeasonStatsItem from '@/components/cricket/SeasonStatsItem.vue'

export default {
  name: 'season',
  props: {
    isMetric: {
      type: Boolean,
      default: false
    }
  },
  components: {
    'SeasonStatsItem': SeasonStatsItem
  },
  data () {
    return {
      seasonKey: this.$route.params.key,
      season: null,
      stats: null,
      state: DataState.newLoading(),

      battingStats: [
        {name: 'Sixes', key: 'sixes'},
        {name: 'Fours', key: 'fours'},
        {name: 'Boundries', key: 'boundries'},
        {name: 'Dots', key: 'dots'},
      ],

      bowlingStats: [
        {name: 'Runs', key: 'runs'},
        {name: 'Boundries', key: 'boundries'},
        {name: 'Dots', key: 'dots'},
      ]
    }
  },
  watch: {
    '$route' (to, from) {
      if (to.params.key !== from.params.key) {
        this.prepareToReopen()
        this.onViewOpenned()
      } else {
        this.refreshView()
      }
    }
  },
  mounted () {
    this.onViewOpenned()
  },
  methods: {
    onViewOpenned () {
      this.seasonKey = this.$route.params.key
      const newTitle = 'loading...'
      AppState.updateActivePage(`cricket_season_${this.seasonKey}_stats`)
      AppState.updateTitles(newTitle, newTitle)

      CricketSeason.fetchStatsWithSeason(this.seasonKey, this.isMetric).then(res => {
        this.season = res.season
        this.stats = res.stats
        const newTitle = `${this.season.name} stats`
        AppState.updateTitles(newTitle, newTitle)

        this.state.changeToLoaded()
      }).catch((error) => {
        console.error(error)
        this.state.changeToError(error)
      })
    },
    prepareToReopen () {
      this.seasonKey = null
      this.season = null
      this.stats = null
    },
    refreshView () {

    },
  }
}
</script>
